'use client';

import { IconFolder, IconMessage, IconSearch } from '@tabler/icons-react';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import type { FileEntry } from 'pc-types';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { editorTabHref } from '@/components/editor-tabs/tab-registry';
import {
  DocumentSearchIcon,
  HighlightedSnippet,
} from '@/components/search/document-search-results';
import { flattenEntryPages } from '@/components/spaces/files/entry-list';
import { useSpaces } from '@/components/spaces/use-spaces';
import { NEW_CHAT_EVENT } from '@/components/global-shortcuts';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { isCommandPaletteShortcut } from '@/lib/global-shortcuts';
import {
  documentSearchHref,
  useDocumentSearch,
} from '@/lib/use-document-search';
import { useFileEntries } from '@/lib/use-file-entries';
import { useAddRecentSpace } from '@/lib/use-recent-spaces';

const MAX_DOCUMENTS = 8;
const MAX_CONTENT_RESULTS = 6;

function entryIconResult(entry: FileEntry) {
  return {
    kind: entry.kind,
    fileType: entry.kind === 'file' ? entry.fileType : null,
  };
}

function includesText(label: string, query: string) {
  return label.toLowerCase().includes(query.toLowerCase());
}

export function CommandPalette() {
  const router = useRouter();
  const pathname = usePathname();
  const addRecentSpace = useAddRecentSpace();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const query = search.trim();
  const nameQuery = useDebouncedValue(query, 150);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isCommandPaletteShortcut(e)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const { data: spaces = [] } = useSpaces();
  const spaceNames = useMemo(
    () => new Map(spaces.map((space) => [space.id, space.name])),
    [spaces]
  );

  // Names across every space the user can open; with no query, the most
  // recently opened documents.
  const entriesQuery = useFileEntries(null, {
    filter: 'all',
    search: nameQuery,
    orderBy: 'lastOpenedAt',
    enabled: open,
  });
  const allDocuments = flattenEntryPages(entriesQuery.data?.pages);
  const documents = allDocuments.slice(0, MAX_DOCUMENTS);

  // One extra result tells us whether the Documents page has more to show.
  const contentSearch = useDocumentSearch(query, {
    spaceId: null,
    limit: MAX_CONTENT_RESULTS + 1,
    enabled: open,
  });
  const contentResults = contentSearch.results.slice(0, MAX_CONTENT_RESULTS);
  const hasMoreResults =
    allDocuments.length > MAX_DOCUMENTS ||
    Boolean(entriesQuery.hasNextPage) ||
    contentSearch.results.length > MAX_CONTENT_RESULTS;

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  const go = (href: string) => {
    close();
    router.push(href);
  };

  const navItems = [
    {
      key: 'home',
      label: 'Home',
      icon: IconMessage,
      onSelect: () => go('/'),
    },
    {
      key: 'new-chat',
      label: 'New chat',
      icon: IconMessage,
      onSelect: () => {
        // Same action as ⌘⇧O: on Home the screen starts a fresh chat in
        // place (navigating to the current route would be a no-op and
        // leave a drafted chat as it was); elsewhere, go there first.
        close();
        const { pathname } = window.location;
        if (pathname === '/' || pathname.startsWith('/chat/')) {
          window.dispatchEvent(new Event(NEW_CHAT_EVENT));
          return;
        }
        router.push('/');
      },
    },
    {
      key: 'documents',
      label: 'Documents',
      icon: IconFolder,
      onSelect: () => go('/documents'),
    },
  ].filter((item) => !query || includesText(item.label, query));

  const matchingSpaces = spaces.filter(
    (space) => !query || includesText(space.name, query)
  );

  // Hide on the full-screen deck editor.
  if (pathname.startsWith('/decks/')) return null;

  return (
    <CommandDialog
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : close())}
      shouldFilter={false}
    >
      <CommandInput
        placeholder="Search documents, spaces, and what's inside them..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList className="max-h-96">
        <CommandEmpty>
          {entriesQuery.isFetching || contentSearch.isSearching
            ? 'Searching…'
            : 'No results found.'}
        </CommandEmpty>

        {navItems.length > 0 && (
          <CommandGroup heading="Go to">
            {navItems.map((item) => (
              <CommandItem
                key={item.key}
                value={`nav:${item.key}`}
                onSelect={item.onSelect}
              >
                <item.icon />
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {documents.length > 0 && (
          <CommandGroup heading={query ? 'Documents' : 'Recent'}>
            {documents.map((entry) => (
              <CommandItem
                key={entry.id}
                value={`doc:${entry.id}`}
                onSelect={() =>
                  go(editorTabHref({ documentId: entry.id, kind: entry.kind }))
                }
              >
                <DocumentSearchIcon result={entryIconResult(entry)} />
                <span className="truncate">{entry.name}</span>
                <span className="ml-auto shrink-0 truncate text-sm text-fg-tertiary">
                  {spaceNames.get(entry.spaceId)}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {contentResults.length > 0 && (
          <CommandGroup heading="Inside files">
            {contentResults.map((result) => {
              const passage = result.passages[0];
              return (
                <CommandItem
                  key={result.documentId}
                  value={`content:${result.documentId}`}
                  onSelect={() => go(documentSearchHref(result, passage))}
                  className="items-start"
                >
                  <DocumentSearchIcon result={result} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{result.name}</span>
                      {passage?.label && (
                        <span className="shrink-0 text-sm text-fg-tertiary">
                          {passage.label}
                        </span>
                      )}
                      <span className="ml-auto shrink-0 truncate text-sm text-fg-tertiary">
                        {result.spaceName}
                      </span>
                    </div>
                    {passage && (
                      <p className="mt-0.5 line-clamp-1 text-sm text-fg-secondary">
                        <HighlightedSnippet
                          text={passage.snippet}
                          query={query}
                        />
                      </p>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {matchingSpaces.length > 0 && (
          <CommandGroup heading="Spaces">
            {matchingSpaces.map((space) => (
              <CommandItem
                key={space.id}
                value={`space:${space.id}`}
                onSelect={() => {
                  addRecentSpace({ id: space.id, name: space.name });
                  go(`/spaces/${space.id}/files`);
                }}
              >
                <IconFolder />
                {space.name}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {query.length >= 2 && hasMoreResults && (
          <CommandGroup>
            <CommandItem
              value="search-everything"
              onSelect={() => go(`/documents?q=${encodeURIComponent(query)}`)}
            >
              <IconSearch />
              <span className="truncate">See all results for “{query}”</span>
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
